import DirectionsCar from "@mui/icons-material/DirectionsCar";
import { Box, Chip, List, ListItem, ListItemText, Typography } from "@mui/material";
import React, { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../../utils/api"; // Import your API module here
import { subscriptionRequiresContract } from "../../utils/subscriptionContractGate";
import { isCoffeePassSubscription, memberVehicleLabel, usesRotatingQr } from "../../utils/coffeePass";
import RotatingRedeemQr from "../../components/RotatingRedeemQr";
import { isPendingCancellation } from "../../utils/subscriptionDisplayStatus";
import StarIcon from "@mui/icons-material/Star";
import ContractDownloadButton from "../../components/ContractDownloadButton";
import ContractAcceptanceModal from "../../components/ContractAcceptanceModal";
// import { List, ListItem } from "material-ui";

const MySubscriptionDetails = ({ subscription, onClose, onRefresh }) => {
  function formatDate(dateString) {
    const options = { year: "numeric", month: "long", day: "numeric" };
    const formattedDate = new Date(dateString).toLocaleDateString(undefined, options);
    return formattedDate;
  }

  const navigate = useNavigate();
  const [redemptionCount, setRedemptionCount] = useState(0); // Corrected from square brackets to parentheses
  const [contractModalOpen, setContractModalOpen] = useState(false);
  const [contractData, setContractData] = useState(null);
  const [freshSubscription, setFreshSubscription] = useState(subscription);
  const [contractInfoRefetchKey, setContractInfoRefetchKey] = useState(0);
  const [contractSigned, setContractSigned] = useState(false); // Track if contract is signed
  const [checkingContract, setCheckingContract] = useState(true); // Track contract check status
  const isInactiveOrDeleted = !subscription.active || subscription.deleted === "yes";
  const isCoffeePass = isCoffeePassSubscription(subscription);
  const isRotatingQr = usesRotatingQr(subscription);
  const vehicleLabel = memberVehicleLabel(subscription);

  /** After contract is signed, GET /subscription/:id includes qrcode; list payload may still have qrcode cleared. */
  const displaySubscription = freshSubscription || subscription;

  const displaySubscriptionForQr = useMemo(() => {
    if (!displaySubscription) return displaySubscription;
    if (subscriptionRequiresContract(subscription.product, subscription) && !contractSigned) {
      return { ...displaySubscription, qrcode: null };
    }
    return displaySubscription;
  }, [displaySubscription, contractSigned, subscription.product?.type]);

  // Check contract status before showing QR code
  useEffect(() => {
    const checkContractStatus = async () => {
      setCheckingContract(true);
      
      // Only check for subscription-type products
      if (subscriptionRequiresContract(subscription.product, subscription)) {
        try {
          const contractResponse = await api(`v1/contract/subscription/${subscription._id}`, "GET");
          if (contractResponse.data.success) {
            const isSigned = contractResponse.data.data.isSigned;
            const requiresContract = contractResponse.data.data.requiresContract;
            
            console.log("Contract check result:", {
              isSigned,
              requiresContract,
            });
            
            const fullySigned = isSigned || !requiresContract;
            setContractSigned(fullySigned);

            if (!fullySigned) {
              setFreshSubscription((prev) => (prev ? { ...prev, qrcode: null } : prev));
            }

            // If contract is required but not signed, show modal
            if (requiresContract && !isSigned) {
              const contract = contractResponse.data.data.contract;
              if (contract && (contract.status === "pending" || contract.contractStatus === "pending")) {
                setContractData({ contract });
                setContractModalOpen(true);
              }
            }
          } else {
            setContractSigned(false);
            setFreshSubscription((prev) => (prev ? { ...prev, qrcode: null } : prev));
          }
        } catch (error) {
          console.error("Error checking contract status:", error);
          setContractSigned(false);
          setFreshSubscription((prev) => (prev ? { ...prev, qrcode: null } : prev));
        }
      } else {
        // Non-subscription products don't require contracts
        setContractSigned(true);
      }
      
      setCheckingContract(false);
    };

    checkContractStatus();
  }, [subscription._id, subscription.product?.type]);

  useEffect(() => {
    if (subscriptionRequiresContract(subscription.product, subscription) && !contractSigned) {
      setFreshSubscription((prev) => (prev ? { ...prev, qrcode: null } : prev));
    }
  }, [contractSigned, subscription.product, subscription.note]);

  // Fetch fresh subscription data when component mounts
  useEffect(() => {
    const fetchFreshSubscription = async () => {
      try {
        const response = await api(`v1/subscription/${subscription._id}`, "GET");
        if (response.data.success) {
          let sub = response.data.data.subscription;
          if (subscriptionRequiresContract(subscription.product, subscription) && !contractSigned) {
            sub = { ...sub, qrcode: null };
          }
          setFreshSubscription(sub);
        }
      } catch (error) {
        console.error("Error fetching fresh subscription:", error);
      }
    };

    fetchFreshSubscription();
  }, [subscription._id, contractSigned, subscription.product?.type]);

  useEffect(() => {
    const fetchRedemptionCount = async () => {
      // Added async keyword here
      if (subscription.product.type === "package") {
        try {
          const redemptionCount = await api(`v1/subscription/${subscription._id}/redemptions`, "GET"); // Assuming you need to make a GET request
          if (redemptionCount && redemptionCount.data.success) {
            setRedemptionCount(redemptionCount.data.data.redemptions.length);
          }
        } catch (error) {
          console.error("API error:", error);
        }
      }
    };

    fetchRedemptionCount(); // Call the async function
  }, [subscription]); // Add 'subscription' as a dependency to avoid infinite re-renders

  const handleSignContract = (contractInfo) => {
    console.log("Opening contract signing modal for:", contractInfo);
    setContractData(contractInfo);
    setContractModalOpen(true);
  };

  const handleContractAccepted = async (acceptedContractData) => {
    console.log("Contract accepted:", acceptedContractData);
    setContractSigned(true); // Mark contract as signed
    setContractModalOpen(false);
    setContractData(null);
    setContractInfoRefetchKey((k) => k + 1);
    try {
      const response = await api(`v1/subscription/${subscription._id}`, "GET");
      if (response.data.success) {
        setFreshSubscription(response.data.data.subscription);
      }
    } catch (error) {
      console.error("Error refreshing subscription after contract acceptance:", error);
    }
    if (onRefresh) {
      onRefresh();
    }
  };

  const handleContractModalClose = () => {
    if (contractSigned) {
      setContractModalOpen(false);
      setContractData(null);
    } else {
      setContractModalOpen(false);
      setContractData(null);
      setFreshSubscription((prev) => (prev ? { ...prev, qrcode: null } : prev));
    }
  };

  return (
    <div className="flex flex-column gap-4 items-start w-full justify-center items-center pt-2">
      <Typography variant="h4" fontWeight={500} sx={{ color: "black" }}>
        {subscription.product.name}
      </Typography>
      <Typography variant="body1" fontWeight={500} sx={{ color: "grey" }}>
        {subscription.product.description}
      </Typography>
      {/* Linked vehicle: full-width prominent row, rego only */}
      {vehicleLabel && (
        <Box
          className="flex items-center gap-2 w-full py-2 px-3 rounded-lg"
          sx={{ bgcolor: "grey.100", width: "100%", maxWidth: 400 }}
        >
          <DirectionsCar sx={{ color: "text.secondary" }} />
          <Typography variant="body2" fontWeight={500} sx={{ color: "text.primary" }}>
            Linked to: {vehicleLabel}
          </Typography>
        </Box>
      )}
      {isCoffeePass && (
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          Valid at participating cafes. One coffee every 24 hours.
        </Typography>
      )}
      {/* Only show QR code if subscription is not expired */}
      {(() => {
        const now = new Date();

        // Check if validUntilDateUTC is valid (not null, not epoch)
        let isExpired = false;
        if (subscription.validUntilDateUTC) {
          const expiryDate = new Date(subscription.validUntilDateUTC);
          const isValidExpiryDate = !isNaN(expiryDate.getTime()) && expiryDate.getFullYear() !== 1970;
          if (isValidExpiryDate) {
            isExpired = expiryDate < now;
          }
        }

        // Check if validFromDateUTC is valid and in the future
        let isFuture = false;
        if (subscription.validFromDateUTC) {
          const fromDate = new Date(subscription.validFromDateUTC);
          const isValidFromDate = !isNaN(fromDate.getTime()) && fromDate.getFullYear() !== 1970;
          if (isValidFromDate) {
            isFuture = fromDate > now;
          }
        }

        const isValid = !isExpired && !isFuture;
        const hasQRCode =
          displaySubscriptionForQr?.qrcode && displaySubscriptionForQr.qrcode.trim() !== "";

        // Only show QR code if contract is signed (for subscription-type products)
        const canShowQRCode =
          !subscriptionRequiresContract(subscription.product, subscription) || contractSigned;

        if (checkingContract) {
          return (
            <div className="border-1 shadow-2xl rounded-3xl p-8 bg-gray-100 text-center">
              <Typography variant="body1" fontWeight={500} sx={{ color: "grey" }}>
                Checking contract status...
              </Typography>
            </div>
          );
        }

        if (isInactiveOrDeleted) {
          return (
            <div className="border-1 shadow-2xl rounded-3xl p-8 bg-gray-100 text-center">
              <Typography variant="body1" fontWeight={500} sx={{ color: "grey" }}>
                QR code is hidden for inactive or deleted subscriptions
              </Typography>
            </div>
          );
        }

        // Live, server-issued code. Never falls back to the stored PNG.
        if (isRotatingQr && isValid && canShowQRCode) {
          return <RotatingRedeemQr subscriptionId={subscription._id} size={250} />;
        }

        if (isValid && hasQRCode && canShowQRCode) {
          return (
            <img
              src={displaySubscriptionForQr.qrcode}
              width={250}
              className="border-1 shadow-2xl rounded-3xl"
              alt="Subscription QR code"
            />
          );
        } else if (isValid && hasQRCode && !canShowQRCode) {
          // Contract required but not signed
          return (
            <div className="border-1 shadow-2xl rounded-3xl p-8 bg-gray-100 text-center">
              <Typography variant="body1" fontWeight={500} sx={{ color: "grey" }}>
                QR code will be available after signing the subscription contract
              </Typography>
              <Typography variant="body2" sx={{ color: "grey", mt: 1, fontSize: "0.75rem" }}>
                Please sign the contract to access your QR code
              </Typography>
            </div>
          );
        } else if (isExpired && subscription.validUntilDateUTC) {
          const expiryDate = new Date(subscription.validUntilDateUTC);
          const isValidExpiryDate = !isNaN(expiryDate.getTime()) && expiryDate.getFullYear() !== 1970;
          return (
            <div className="border-1 shadow-2xl rounded-3xl p-8 bg-gray-100 text-center">
              <Typography variant="body1" fontWeight={500} sx={{ color: "grey" }}>
                {isValidExpiryDate
                  ? `This subscription has expired on ${formatDate(subscription.validUntilDateUTC)}`
                  : "This subscription has expired"}
              </Typography>
            </div>
          );
        } else if (isFuture && subscription.validFromDateUTC) {
          const fromDate = new Date(subscription.validFromDateUTC);
          const isValidFromDate = !isNaN(fromDate.getTime()) && fromDate.getFullYear() !== 1970;
          return (
            <div className="border-1 shadow-2xl rounded-3xl p-8 bg-gray-100 text-center">
              <Typography variant="body1" fontWeight={500} sx={{ color: "grey" }}>
                {isValidFromDate
                  ? `QR code will be available from ${formatDate(subscription.validFromDateUTC)}`
                  : "QR code will be available soon"}
              </Typography>
            </div>
          );
        } else if (!hasQRCode) {
          return (
            <div className="border-1 shadow-2xl rounded-3xl p-8 bg-gray-100 text-center">
              <Typography variant="body1" fontWeight={500} sx={{ color: "grey" }}>
                QR code not available
              </Typography>
              <Typography variant="body2" sx={{ color: "grey", mt: 1, fontSize: "0.75rem" }}>
                Please contact support if this issue persists
              </Typography>
            </div>
          );
        } else {
          return (
            <div className="border-1 shadow-2xl rounded-3xl p-8 bg-gray-100 text-center">
              <Typography variant="body1" fontWeight={500} sx={{ color: "grey" }}>
                QR code not available
              </Typography>
            </div>
          );
        }
      })()}
      {/* Only show ID if QR code is available */}
      {(() => {
        const now = new Date();
        let isExpired = false;
        if (subscription.validUntilDateUTC) {
          const expiryDate = new Date(subscription.validUntilDateUTC);
          const isValidExpiryDate = !isNaN(expiryDate.getTime()) && expiryDate.getFullYear() !== 1970;
          if (isValidExpiryDate) {
            isExpired = expiryDate < now;
          }
        }
        let isFuture = false;
        if (subscription.validFromDateUTC) {
          const fromDate = new Date(subscription.validFromDateUTC);
          const isValidFromDate = !isNaN(fromDate.getTime()) && fromDate.getFullYear() !== 1970;
          if (isValidFromDate) {
            isFuture = fromDate > now;
          }
        }
        const isValid = !isExpired && !isFuture;
        const hasQRCode =
          displaySubscriptionForQr?.qrcode && displaySubscriptionForQr.qrcode.trim() !== "";
        const canShowQRCode =
          !subscriptionRequiresContract(subscription.product, subscription) || contractSigned;
        
        // Hide code when subscription is inactive or deleted, and never print the id for a live QR
        if (isInactiveOrDeleted || isRotatingQr) {
          return null;
        }

        // Only show ID if QR code is available and can be shown
        if (isValid && hasQRCode && canShowQRCode) {
          return (
            <Typography
              className="uppercase"
              variant="body1"
              fontWeight={500}
              sx={{ color: "purple", bgcolor: "#e0e0e0", padding: ".5em;" }}
            >
              {subscription?._id}
            </Typography>
          );
        }
        return null;
      })()}
      <div className="flex gap-2 items-center">
        <Chip
          sx={{ fontSize: "16px" }}
          label={
            isPendingCancellation(subscription)
              ? "Pending Cancellation"
              : subscriptionRequiresContract(subscription.product, subscription) &&
                  subscription.active &&
                  !contractSigned
              ? "Pending"
              : subscription.active
              ? "Active"
              : "Inactive"
          }
          color={
            isPendingCancellation(subscription)
              ? "warning"
              : subscriptionRequiresContract(subscription.product, subscription) &&
                  subscription.active &&
                  !contractSigned
              ? "warning"
              : subscription.active
              ? "success"
              : "default"
          }
        />
        {(() => {
          if (!subscription.validUntilDateUTC) {
            return <span>Expires: No expiry date set</span>;
          }
          const expiryDate = new Date(subscription.validUntilDateUTC);
          // Check if date is valid and not epoch (January 1, 1970)
          const isValidDate = !isNaN(expiryDate.getTime()) && expiryDate.getFullYear() !== 1970;

          if (isValidDate) {
            return <span>Expires: {formatDate(subscription.validUntilDateUTC)}</span>;
          } else {
            return <span>Expires: No expiry date set</span>;
          }
        })()}
      </div>

      {/* Contract Download Section - Only show for subscription-type products */}
      {subscriptionRequiresContract(subscription.product, subscription) && (
        <div className="w-full mt-4">
          <ContractDownloadButton
            subscriptionId={subscription._id}
            subscription={subscription}
            onSignContract={handleSignContract}
            onCloseModal={onClose}
            contractRefetchKey={contractInfoRefetchKey}
          />
        </div>
      )}
      {subscription.product.type === "package" && (
        <div className="flex items-center text-lg">
          <StarIcon fontSize="large" style={{ color: "#faaf00", verticalAlign: "middle" }} />
          &nbsp;{redemptionCount} of {subscription.product.packageCount} redemptions used
        </div>
      )}
      <div className="how-to-redeem-instruction-wrap flex flex-column w-full">
        <Typography fontSize={16} fontWeight={500}>
          How to Redeem
        </Typography>
        {isCoffeePass ? (
          <List sx={{ listStyle: "decimal", pl: 4 }}>
            <ListItem sx={{ display: "list-item", p: 0 }}>
              <ListItemText primary="Order at a participating cafe" component="span" />
            </ListItem>
            <ListItem sx={{ display: "list-item", p: 0 }}>
              <ListItemText
                primary="Open this pass at the counter and show the live QR code to cafe staff"
                component="span"
              />
            </ListItem>
            <ListItem sx={{ display: "list-item", p: 0 }}>
              <ListItemText primary="Enjoy your coffee. Your next one is available after 24 hours" component="span" />
            </ListItem>
          </List>
        ) : (
          <List sx={{ listStyle: "decimal", pl: 4 }}>
            <ListItem sx={{ display: "list-item", p: 0 }}>
              <ListItemText primary="Find a participating location" component="span" />
            </ListItem>

            <ListItem sx={{ display: "list-item", p: 0 }}>
              <ListItemText
                primary={
                  isRotatingQr
                    ? "A staff member will identify your vehicle and scan your live QR code"
                    : "A staff member will identify your vehicle and scan your QR code"
                }
                component="span"
              />
            </ListItem>

            <ListItem sx={{ display: "list-item", p: 0 }}>
              <ListItemText primary="Enjoy your the rest of your day" component="span" />
            </ListItem>
          </List>
        )}
      </div>
      {freshSubscription.redemptions && (
        <div className="flex flex-column w-full text-xs">
          {freshSubscription.redemptions && (
            <Typography fontSize={14} className="font-bold border-b-2">
              Redemption History
            </Typography>
          )}
          {freshSubscription.redemptions
            .filter((redemption) => {
              if (!redemption || redemption.active === false) return false;
              // Only show redemptions for this subscription's vehicle (exclude redemptions
              // that point to another customer's vehicle, e.g. after subscription was moved)
              const subVehicleId = freshSubscription.vehicle?._id || freshSubscription.vehicle;
              const redemptionVehicleId = redemption.vehicle?._id || redemption.vehicle;
              if (!subVehicleId || !redemptionVehicleId) return true; // keep if IDs missing to avoid hiding valid rows
              return String(subVehicleId) === String(redemptionVehicleId);
            })
            .map(
              (redemption) =>
                redemption && (
                  <div className="py-1">
                    <span>
                      {redemption?.redemptionDateUTC
                        ? `${new Date(redemption.redemptionDateUTC).toLocaleTimeString()} on ${formatDate(
                            redemption.redemptionDateUTC
                          )}`
                        : "No expiry available"}
                    </span>
                    <span>
                      {" "}
                      at <Link>{redemption.merchantStore.name}</Link>
                    </span>
                  </div>
                )
            )}
        </div>
      )}

      {/* Contract Acceptance Modal */}
      {contractData && (
        <ContractAcceptanceModal
          isOpen={contractModalOpen}
          onClose={handleContractModalClose}
          contractData={contractData.contract}
          onContractAccepted={handleContractAccepted}
        />
      )}
    </div>
  );
};

export default MySubscriptionDetails;
